import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; push?: unknown; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

/**
 * Facebook/Instagram Meta Pixel — admin-configurable (Settings → Integrations
 * → Meta Pixel), so the ID can be changed without a code deploy. Loaded
 * directly here rather than through GTM (GTM owns GA4 loading in index.html,
 * but no Facebook tag exists in that container, and routing a live-editable
 * value through GTM would still need a manual GTM console edit) — this way
 * changing the ID in Settings takes effect immediately, no developer needed.
 *
 * Fires an initial PageView on load, then one more on every client-side route
 * change — a plain one-time base-code install only ever fires the first
 * PageView, so every SPA navigation after that would go untracked otherwise.
 */
const MetaPixelLoader = () => {
  const { metaPixelId: pixelId } = useSiteSettings();
  const location = useLocation();
  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pixelId) return;

    if (loadedIdRef.current !== pixelId) {
      (function (f: Window, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n: any = (f.fbq = function (...args: unknown[]) {
          n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq?.('init', pixelId);
      loadedIdRef.current = pixelId;
    }

    window.fbq?.('track', 'PageView');
  }, [pixelId, location.pathname]);

  return null;
};

export default MetaPixelLoader;
